import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { AxiosError } from 'axios';
import { authService } from '../services/authService';
import { userService } from '@/features/user/services/userService';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types/user.types';
import { transformUserFromBackend } from '@/utils/transformers';
import { RegisterData } from '../types/auth.types';
import { USE_MOCK_AUTH, mockLogin, mockRegister } from '@/mocks/authMock';
import {
  trackUserLoggedIn,
  trackUserLoggedOut,
  trackUserSignedUp,
} from '@/lib/analytics/events';

interface ApiError {
  detail?: string | Array<{
    loc: string[];
    msg: string;
    type: string;
  }>;
  message?: string;
}

export const useLogin = () => {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();

  return useMutation({
    mutationFn: async (credentials: { email: string; password: string }) => {
      // Use mock auth if enabled
      if (USE_MOCK_AUTH) {
        const mockData = await mockLogin(credentials.email, credentials.password);
        return {
          tokens: {
            access_token: mockData.accessToken,
            refresh_token: mockData.refreshToken,
            token_type: 'bearer',
          },
          user: mockData.user,
        };
      }

      // Real API calls
      // Step 1: Login to get tokens
      const loginResponse = await authService.login(credentials);

      // Step 3: Fetch user data with the new access token
      const userResponse = await userService.getCurrentUser(loginResponse.access_token);

      return {
        tokens: loginResponse,
        user: transformUserFromBackend(userResponse),
      };
    },
    onSuccess: (data) => {
      setAuth(data.user, data.tokens.access_token, data.tokens.refresh_token);
      trackUserLoggedIn(data.user.role);
      message.success('Login successful');

      // Role-based redirect
      const redirectMap: Record<UserRole, string> = {
        [UserRole.CUSTOMER]: '/home',
        [UserRole.FARMER]: '/farmer',
        [UserRole.DELIVERY_AGENT]: '/delivery',
        [UserRole.SUPPLIER]: '/supplier',
        [UserRole.ADMIN]: '/admin',
      };

      const targetPath = redirectMap[data.user.role] || '/login';

      // Use replace to avoid navigation issues
      setTimeout(() => {
        navigate(targetPath, { replace: true });
      }, 100);
    },
    onError: (error: AxiosError<ApiError>) => {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Login failed. Please check your credentials.';

      if (typeof detail === 'string') {
        // e.g. "Your farmer account is pending admin approval..."
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail[0]?.msg) {
        errorMessage = detail[0].msg;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      message.error(errorMessage, 6);
    },
  });
};

export const useRegister = () => {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (userData: RegisterData) => {
      // Use mock auth if enabled
      if (USE_MOCK_AUTH) {
        return mockRegister({
          ...userData,
          phone_number: userData.phone,
          role: userData.role as 'customer' | 'farmer' | 'delivery_agent' | 'supplier',
        });
      }

      // Real API call
      return authService.register(userData);
    },
    onSuccess: (_data, variables) => {
      trackUserSignedUp(variables.role);
      if (variables.role === 'farmer') {
        message.success(
          'Application submitted! Your farmer account is pending admin approval — you will receive an SMS once approved, then you can log in.',
          8
        );
      } else {
        message.success('Registration successful! Please login with your credentials.');
      }
      // Redirect to login page since register doesn't return tokens
      navigate('/login');
    },
    onError: (error: AxiosError<ApiError>) => {
      let errorMessage = 'Registration failed. Please try again.';
      const errorDetail = error.response?.data?.detail;

      if (typeof errorDetail === 'string') {
        // Handle errors like: {"detail": "Email already registered"}
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail) && errorDetail[0]?.msg) {
        // Handle validation errors like: {"detail":[{"msg": "..."}]}
        errorMessage = errorDetail[0].msg;
      } else if (error.response?.data?.message) {
        // Fallback for other formats
        errorMessage = error.response.data.message;
      }

      message.error(errorMessage);
    },
  });
};

export const useLogout = () => {
  const navigate = useNavigate();
  const { clearAuth } = useAuthStore();

  return useMutation({
    mutationFn: async () => {
      // Clear auth state (backend doesn't have logout endpoint based on docs)
      clearAuth();
    },
    onSuccess: () => {
      trackUserLoggedOut('manual');
      message.success('Logged out successfully');
      navigate('/login');
    },
  });
};
